export interface InventoryStoreAddress {
  address_line?: string;
  province?: string;
  province_name?: string;
  ward?: string;
  ward_name?: string;
  free_address?: string;
  full_address?: string;
}

export interface InventoryStoreStatus {
  id?: string;
  code?: string;
  name?: string;
  color?: string;
}

export type InventoryStoreAddressValue = string | InventoryStoreAddress;
export type InventoryStoreStatusValue = string | InventoryStoreStatus;

export interface InventoryStoreRow {
  id: string;
  code: string;
  name: string;
  status: string;
  status_label: string;
  status_color?: string;
  company_id: string;
  company_name: string;
  address?: InventoryStoreAddressValue;
  created_at?: string;
  updated_at?: string;
}

export interface InventoryStoreFormData {
  code: string;
  name: string;
  status: string;
  company_id: string;
  address: InventoryStoreAddress;
}

export interface InventoryStoreListResponse {
  results?: InventoryStoreRow[];
  count?: number;
  page?: number;
  page_size?: number;
  total_pages?: number;
  has_next?: boolean;
  has_previous?: boolean;
}

export interface InventoryStoreQueryParams {
  search?: string;
  status?: string;
  company_id?: string;
  page?: number;
  page_size?: number;
}

export interface InventoryStorePayload {
  code: string;
  name: string;
  status: string;
  address?: string;
  address_obj?: InventoryStoreAddress;
  company: string;
}

export const createEmptyStoreAddress = (): InventoryStoreAddress => ({
  address_line: '',
  province: '',
  ward: '',
  free_address: '',
});

export const formatStoreAddress = (address?: InventoryStoreAddressValue | null): string => {
  if (!address) {
    return '';
  }

  if (typeof address === 'string') {
    return address.trim();
  }

  const directAddress = String(address.free_address || address.full_address || '').trim();
  if (directAddress) {
    return directAddress;
  }

  return [
    address.address_line,
    address.ward_name || address.ward,
    address.province_name || address.province,
  ]
    .map((part) => String(part || '').trim())
    .filter(Boolean)
    .join(', ');
};

export const hasMeaningfulStoreFormInput = (formData?: InventoryStoreFormData | null): boolean => {
  if (!formData) {
    return false;
  }

  const address = formData.address || {};

  return Boolean(
    String(formData.code || '').trim() ||
    String(formData.name || '').trim() ||
    String(address.address_line || '').trim() ||
    String(address.ward || '').trim() ||
    String(address.free_address || '').trim()
  );
};

const normalizeStoreFormSnapshot = (formData?: InventoryStoreFormData | null) => {
  const address = formData?.address || {};

  return {
    code: String(formData?.code || '').trim(),
    name: String(formData?.name || '').trim(),
    status: String(formData?.status || '').trim(),
    company_id: String(formData?.company_id || '').trim(),
    address_line: String(address.address_line || '').trim(),
    province: String(address.province || '').trim(),
    ward: String(address.ward || '').trim(),
    free_address: String(address.free_address || '').trim(),
  };
};

export const hasStoreFormChanged = (
  current?: InventoryStoreFormData | null,
  initial?: InventoryStoreFormData | null
): boolean => {
  if (!current || !initial) {
    return false;
  }

  return JSON.stringify(normalizeStoreFormSnapshot(current)) !== JSON.stringify(normalizeStoreFormSnapshot(initial));
};

export const storeStatusOptions = [
  { id: 'all', name: 'Tất cả' },
  { id: 'active', name: 'Hoạt động' },
  { id: 'inactive', name: 'Dừng hoạt động' },
];

export const createDefaultStoreForm = (companyId: string): InventoryStoreFormData => ({
  code: '',
  name: '',
  status: 'active',
  company_id: companyId,
  address: createEmptyStoreAddress(),
});
